/**
 * «История культур на поле» — посевы по сезонам с планом и фактом урожайности
 * (ц/га) и колонкой «Зона» (чипы), когда поле разбито на зоны. Read-only.
 *
 * Урожайность приходит с backend уже в ц/га (сервер ×10 из т/га). Единицы
 * подписаны в шапке — пользователь видит привычные ц/га.
 */
import { Badge } from '@/components/ui/badge';
import { CropChip } from '@/components/ui/crop-chip';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GrowingGoalChips } from './GrowingGoalChips';
import { fieldStateLabel } from './constants';
import { useFieldStateLabel, type CodeLabeler } from '@/lib/reference/fieldCatalogs';
import { fmtYieldCHa } from './rotationLogic';
import type { CropHistoryRow } from './types';

interface Props {
  rows: CropHistoryRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Показывать ли колонку «Зона» (поле разбито на зоны). */
  showZone: boolean;
}

/** MC-3 — компактный лейбл списка сортов: максимум 3, дальше «…».
 *  Пусто (нет привязки в sowing_varieties или строка «Не занимали») → «—». */
function varietyCell(names: string[] | undefined): string {
  if (!names || names.length === 0) return '—';
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} …`;
}

function rowCropLabel(r: CropHistoryRow, fallowLabel: CodeLabeler): string {
  // Строка, которую посчитала система: поле никто не занял до конца сезона.
  // Это НЕ пар — пар выбирают, с видом (чёрный / сидеральный / занятый).
  // Подпись даёт сервер («Не занимали»), чтобы слово было одно на всех клиентов.
  if (r.is_derived) return r.status_label ?? 'Не занимали';
  // Пар/АМП — занятие без культуры (АМП паром не называем).
  if (r.occupancy_type === 'fallow') {
    return fieldStateLabel(r.fallow_kind, fallowLabel);
  }
  return r.crop_name ?? 'Культура';
}

export function CropHistoryTable({ rows, isLoading, isError, showZone }: Props) {
  // Вид пара подписываем из справочника reference.field_states.
  const fallowLabel = useFieldStateLabel();
  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Не удалось загрузить историю культур.</AlertDescription>
      </Alert>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <p className="py-2 text-sm text-muted-foreground">
        Истории культур пока нет — она появится после первого посева.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Сезон</th>
            <th className="py-2 pr-3 font-medium">Культура</th>
            <th className="py-2 pr-3 font-medium">Сорт</th>
            {showZone && <th className="py-2 pr-3 font-medium">Зона</th>}
            <th className="py-2 pr-3 text-right font-medium">План, ц/га</th>
            <th className="py-2 text-right font-medium">Факт, ц/га</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            // Ключ — row_id: у посчитанной строки «Не занимали» тот же
            // sowing_id, что у занятия-источника (она про тот же посев), и по
            // sowing_id React ловил бы duplicate key.
            <tr
              key={r.row_id ?? r.sowing_id}
              className="border-b border-border last:border-0"
              data-testid={r.is_derived ? 'history-row-derived' : 'history-row'}
            >
              <td className="py-2 pr-3 font-medium">{r.season_year ?? '—'}</td>
              <td className="py-2 pr-3">
                {r.is_derived ? (
                  // Приглушённый тон + объяснение: агроном сразу видит, что
                  // строку не заводили руками, и почему она здесь.
                  <>
                    <span className="text-muted-foreground">
                      {rowCropLabel(r, fallowLabel)}
                    </span>
                    {r.note && (
                      <div className="text-xs leading-tight text-muted-foreground">
                        {r.note}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <CropChip name={rowCropLabel(r, fallowLabel)} iconSize={16} />
                    <GrowingGoalChips goals={r.growing_goals} className="mt-1" />
                  </>
                )}
              </td>
              <td className="py-2 pr-3 text-sm text-muted-foreground">
                {varietyCell(r.variety_names)}
              </td>
              {showZone && (
                <td className="py-2 pr-3">
                  <Badge variant="outline" className="text-muted-foreground">
                    {r.zone_name ?? 'всё поле'}
                  </Badge>
                </td>
              )}
              <td className="py-2 pr-3 text-right tabular-nums">
                {fmtYieldCHa(r.planned_yield_c_ha)}
              </td>
              <td className="py-2 text-right font-medium tabular-nums">
                {fmtYieldCHa(r.actual_yield_c_ha)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
